import { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetCategoriesQuery,
  useGetBrandsQuery,
  useGetSuppliersQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
} from "../../../../redux/api";
import type { Product as ProductModel } from "../../../../redux/api";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import type { EditedImage } from "../../../../hooks/useImageEdit";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { GalleryPickerModal } from "../../../../components/GalleryPickerModal";
import { ImagePreviewModal } from "../../../../components/ImagePreviewModal";
import { ImageEditorModal } from "../../../../components/ImageEditorModal";
import { IconEdit } from "../../../../components/icons";
import { statusTone } from "../../../../utils/status";
import { Field } from "../../../../components/Field";
import type { Asset } from "../../../../redux/api";

interface ProductFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type ProductForm = {
  sku: string;
  productCode: string;
  barcode: string;
  qrCode: string;
  productName: string;
  shortName: string;
  description: string;

  categoryId: string;
  subCategoryId: string;
  brandId: string;
  supplierId: string;
  manufacturer: string;
  tags: string;

  purchasePrice: string;
  sellingPrice: string;
  currency: string;
  taxPercentage: string;
  taxCode: string;
  discountPercentage: string;
  isTaxable: boolean;

  inventoryTracking: string;
  totalStock: string;
  reorderLevel: string;
  maximumStockLevel: string;
  allowBackOrder: boolean;
  batchTracking: boolean;
  serialTracking: boolean;
  expiryTracking: boolean;
  unit: string;

  length: string;
  width: string;
  height: string;
  weight: string;
  dimensionUnit: string;
  weightUnit: string;
  countryOfOrigin: string;
  hsnCode: string;
  upc: string;
  ean: string;

  status: string;
  isFeatured: boolean;
  notes: string;
};

const emptyForm: ProductForm = {
  sku: "",
  productCode: "",
  barcode: "",
  qrCode: "",
  productName: "",
  shortName: "",
  description: "",

  categoryId: "",
  subCategoryId: "",
  brandId: "",
  supplierId: "",
  manufacturer: "",
  tags: "",

  purchasePrice: "",
  sellingPrice: "",
  currency: "INR",
  taxPercentage: "",
  taxCode: "",
  discountPercentage: "",
  isTaxable: true,

  inventoryTracking: "QUANTITY",
  totalStock: "",
  reorderLevel: "",
  maximumStockLevel: "",
  allowBackOrder: false,
  batchTracking: false,
  serialTracking: false,
  expiryTracking: false,
  unit: "PCS",

  length: "",
  width: "",
  height: "",
  weight: "",
  dimensionUnit: "cm",
  weightUnit: "kg",
  countryOfOrigin: "",
  hsnCode: "",
  upc: "",
  ean: "",

  status: "ACTIVE",
  isFeatured: false,
  notes: "",
};

const formFromProduct = (p: ProductModel): ProductForm => ({
  sku: p.sku ?? "",
  productCode: p.productCode ?? "",
  barcode: p.barcode ?? "",
  qrCode: p.qrCode ?? "",
  productName: p.productName ?? "",
  shortName: p.shortName ?? "",
  description: p.description ?? "",

  categoryId: p.categoryId ?? "",
  subCategoryId: p.subCategoryId ?? "",
  brandId: p.brandId ?? "",
  supplierId: p.supplierId ?? "",
  manufacturer: p.manufacturer ?? "",
  tags: (p.tags ?? []).join(", "),

  purchasePrice: p.purchasePrice != null ? String(p.purchasePrice) : "",
  sellingPrice: p.sellingPrice != null ? String(p.sellingPrice) : "",
  currency: p.currency ?? "INR",
  taxPercentage: p.taxPercentage != null ? String(p.taxPercentage) : "",
  taxCode: p.taxCode ?? "",
  discountPercentage:
    p.discountPercentage != null ? String(p.discountPercentage) : "",
  isTaxable: p.isTaxable ?? true,

  inventoryTracking: p.inventoryTracking ?? "QUANTITY",
  totalStock: p.totalStock != null ? String(p.totalStock) : "",
  reorderLevel: p.reorderLevel != null ? String(p.reorderLevel) : "",
  maximumStockLevel:
    p.maximumStockLevel != null ? String(p.maximumStockLevel) : "",
  allowBackOrder: p.allowBackOrder ?? false,
  batchTracking: p.batchTracking ?? false,
  serialTracking: p.serialTracking ?? false,
  expiryTracking: p.expiryTracking ?? false,
  unit: p.unit ?? "PCS",

  length: p.dimensions?.length != null ? String(p.dimensions.length) : "",
  width: p.dimensions?.width != null ? String(p.dimensions.width) : "",
  height: p.dimensions?.height != null ? String(p.dimensions.height) : "",
  weight: p.dimensions?.weight != null ? String(p.dimensions.weight) : "",
  dimensionUnit: p.dimensions?.dimensionUnit ?? "cm",
  weightUnit: p.dimensions?.weightUnit ?? "kg",
  countryOfOrigin: p.countryOfOrigin ?? "",
  hsnCode: p.hsnCode ?? "",
  upc: p.upc ?? "",
  ean: p.ean ?? "",

  status: p.status ?? "ACTIVE",
  isFeatured: p.isFeatured ?? false,
  notes: p.notes ?? "",
});

const num = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

// Mirrors PRODUCT_MAX_IMAGES in services' product/model.ts, which is what
// actually enforces the cap — this only fails the add early with a nicer
// message. Keep the two in step.
const MAX_IMAGES = 6;

// Inventory → Product add/edit popup, organized into Identity / Classification
// / Pricing / Inventory / Logistics / Status sections. Creating a product
// keeps the popup open and switches into edit mode (rather than closing) so
// images — which need an existing id — can be added right away.
export const ProductFormModal = ({ open, id, onClose }: ProductFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetProductQuery(activeId ?? "", {
    skip: !activeId,
  });

  const { data: categories = [] } = useGetCategoriesQuery();
  const { data: brands = [] } = useGetBrandsQuery();
  const { data: suppliers = [] } = useGetSuppliersQuery();

  const [createProduct, { isLoading: creating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
  const { upload, uploading, error: uploadError } = useImageUpload("PRODUCT", activeId ?? "");
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Images aren't part of the dirty-tracked `form` above — each add/remove/
  // set-primary action persists immediately (same reasoning as Brand/
  // Organization logo: it's an array of add/remove operations, not a single
  // pending text edit). Read straight from the query result rather than a
  // separate local copy, so it can't drift from what's actually saved.
  const images = existing?.images ?? [];

  // The GraphQL mutation input only accepts the fields declared on
  // ProductImageInput (notably `key`, never `url` — `url` is a freshly
  // presigned value computed on read, not something the server accepts
  // back), so strip it before resubmitting.
  const toImageInput = (img: (typeof images)[number]) => ({
    id: img.id,
    key: img.key,
    thumbnailUrl: img.thumbnailUrl,
    fileName: img.fileName,
    fileSize: img.fileSize,
    mimeType: img.mimeType,
    isPrimary: img.isPrimary,
    sortOrder: img.sortOrder,
    uploadedAt: img.uploadedAt,
  });

  const persistImages = async (next: typeof images) => {
    if (!activeId) return;
    try {
      await updateProduct({ id: activeId, input: { images: next.map(toImageInput) } }).unwrap();
    } catch (err) {
      setFormError((err as { message?: string })?.message ?? "Failed to update images.");
    }
  };

  const handleAddImageFile = async (file: File | undefined) => {
    if (!file || !activeId) return;
    if (images.length >= MAX_IMAGES) {
      setFormError(`A product can have at most ${MAX_IMAGES} images.`);
      return;
    }
    const uploaded = await upload(file);
    if (!uploaded) return;
    await persistImages([
      ...images,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        key: uploaded.key,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
        isPrimary: images.length === 0,
        sortOrder: images.length,
        uploadedAt: new Date().toISOString(),
      },
    ]);
  };

  const handleRemoveImage = (id: string) => persistImages(images.filter((img) => img.id !== id));

  const handleSetPrimaryImage = (id: string) =>
    persistImages(images.map((img) => ({ ...img, isPrimary: img.id === id })));

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<(typeof images)[number] | null>(null);
  const [editingImage, setEditingImage] = useState<(typeof images)[number] | null>(null);

  // The edit is rendered into a new object, so the product is re-pointed at it
  // in place — same slot, same primary flag, same position. The original stays
  // in the Gallery, which is what makes an edit undoable by simply picking the
  // old file again.
  const handleImageEdited = (edited: EditedImage) => {
    if (!editingImage) return;
    persistImages(
      images.map((img) =>
        img.id === editingImage.id
          ? {
              ...img,
              key: edited.key,
              fileName: edited.fileName,
              mimeType: edited.mimeType,
              fileSize: edited.fileSize,
              // The url held here still points at the pre-edit object. It is
              // stripped by toImageInput before this reaches the server, so
              // this only affects what renders in the gap before the update
              // round-trips with a freshly presigned one.
              url: edited.url ?? img.url,
            }
          : img,
      ),
    );
    setEditingImage(null);
  };

  const handlePickFromGallery = (picked: Asset[]) => {
    const next = picked.map((asset, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
      key: asset.key,
      fileName: asset.fileName,
      mimeType: asset.mimeType,
      fileSize: asset.fileSize,
      isPrimary: images.length === 0 && i === 0,
      sortOrder: images.length + i,
      uploadedAt: asset.uploadedAt,
    }));
    persistImages([...images, ...next]);
  };

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromProduct(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromProduct(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  // Top-level categories vs the sub-categories of whichever one is selected.
  const topCategories = useMemo(
    () => categories.filter((c) => !c.parentId),
    [categories],
  );
  const subCategories = useMemo(
    () => categories.filter((c) => c.parentId === form.categoryId),
    [categories, form.categoryId],
  );

  const setField = <K extends keyof ProductForm>(key: K, value: ProductForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setCategory = (categoryId: string) =>
    setForm((prev) => ({ ...prev, categoryId, subCategoryId: "" }));

  const baseline = useMemo(
    () => (existing ? formFromProduct(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.sku.trim()) errors.sku = "SKU is required.";
    if (!form.productCode.trim()) errors.productCode = "Product code is required.";
    if (!form.productName.trim()) errors.productName = "Product name is required.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const hasDimensions =
      form.length || form.width || form.height || form.weight;

    const payload = {
      sku: form.sku.trim(),
      productCode: form.productCode.trim(),
      barcode: form.barcode.trim() || undefined,
      qrCode: form.qrCode.trim() || undefined,
      productName: form.productName.trim(),
      shortName: form.shortName.trim() || undefined,
      description: form.description.trim() || undefined,

      categoryId: form.categoryId || undefined,
      subCategoryId: form.subCategoryId || undefined,
      brandId: form.brandId || undefined,
      supplierId: form.supplierId || undefined,
      manufacturer: form.manufacturer.trim() || undefined,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),

      unit: form.unit.trim() || undefined,
      purchasePrice: num(form.purchasePrice),
      sellingPrice: num(form.sellingPrice),
      currency: form.currency,
      taxPercentage: num(form.taxPercentage),
      taxCode: form.taxCode.trim() || undefined,
      discountPercentage: num(form.discountPercentage),
      isTaxable: form.isTaxable,

      inventoryTracking: form.inventoryTracking as ProductModel["inventoryTracking"],
      reorderLevel: num(form.reorderLevel),
      maximumStockLevel: num(form.maximumStockLevel),
      allowBackOrder: form.allowBackOrder,
      batchTracking: form.batchTracking,
      serialTracking: form.serialTracking,
      expiryTracking: form.expiryTracking,

      dimensions: hasDimensions
        ? {
            length: num(form.length),
            width: num(form.width),
            height: num(form.height),
            weight: num(form.weight),
            dimensionUnit: form.dimensionUnit as ProductModel["dimensions"] extends infer D
              ? D extends { dimensionUnit?: infer U }
                ? U
                : never
              : never,
            weightUnit: form.weightUnit as any,
          }
        : undefined,
      countryOfOrigin: form.countryOfOrigin.trim() || undefined,
      hsnCode: form.hsnCode.trim() || undefined,
      upc: form.upc.trim() || undefined,
      ean: form.ean.trim() || undefined,

      status: form.status as ProductModel["status"],
      isFeatured: form.isFeatured,
      notes: form.notes.trim() || undefined,
    };

    try {
      if (isEdit && activeId) {
        await updateProduct({ id: activeId, input: payload }).unwrap();
        onClose();
      } else {
        const created = await createProduct({ ...payload }).unwrap();
        // Stay open, now in edit mode, so images can be added immediately.
        setActiveId(created.id);
      }
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save product.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit product" : "New product"}
      onClose={onClose}
      xwide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <h3 className="panel__section-title">Identity</h3>
      <div className="form-grid">
        <Field label="SKU *" error={fieldErrors.sku}>
          <input
            value={form.sku}
            onChange={(e) => {
              setField("sku", e.target.value);
              setFieldErrors((prev) => ({ ...prev, sku: "" }));
            }}
          />
        </Field>
        <Field label="Product code *" error={fieldErrors.productCode}>
          <input
            value={form.productCode}
            onChange={(e) => {
              setField("productCode", e.target.value);
              setFieldErrors((prev) => ({ ...prev, productCode: "" }));
            }}
            placeholder="LAP-001"
          />
        </Field>
        <Field label="Product name *" error={fieldErrors.productName}>
          <input
            value={form.productName}
            onChange={(e) => {
              setField("productName", e.target.value);
              setFieldErrors((prev) => ({ ...prev, productName: "" }));
            }}
          />
        </Field>
        <label className="field">
          Short name
          <input
            value={form.shortName}
            onChange={(e) => setField("shortName", e.target.value)}
          />
        </label>
        <label className="field">
          Barcode
          <input
            value={form.barcode}
            onChange={(e) => setField("barcode", e.target.value)}
          />
        </label>
        <label className="field">
          QR code
          <input
            value={form.qrCode}
            onChange={(e) => setField("qrCode", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Description
          <textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
          />
        </label>
      </div>

      <h3 className="panel__section-title">Images</h3>
      <div>
        {!isEdit ? (
          <p className="panel__sub">Save the product first to add images.</p>
        ) : (
          <>
            {uploadError ? <div className="form-error">{uploadError}</div> : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
              {images.map((img) => (
                <div
                  key={img.id}
                  style={{
                    width: 120,
                    border: img.isPrimary ? "2px solid var(--primary, #2563eb)" : "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "0.4rem",
                    textAlign: "center",
                  }}
                >
                  <img
                    src={img.url ?? undefined}
                    alt={img.fileName ?? "Product"}
                    style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                    onClick={() => setPreviewImage(img)}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.35rem" }}>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      style={{ fontSize: "0.7rem", padding: "0.2rem 0.4rem" }}
                      onClick={() => handleSetPrimaryImage(img.id)}
                      disabled={img.isPrimary}
                    >
                      {img.isPrimary ? "Primary" : "Set primary"}
                    </button>
                    <div style={{ display: "flex" }}>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Edit image"
                        title="Crop, rotate and adjust"
                        onClick={() => setEditingImage(img)}
                      >
                        <IconEdit size={14} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Remove image"
                        onClick={() => handleRemoveImage(img.id)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={(e) => handleAddImageFile(e.target.files?.[0])}
            />
            <div className="row-actions">
              <button
                type="button"
                className="btn"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploading || images.length >= 5}
              >
                {uploading ? "Uploading…" : images.length >= 5 ? "Maximum 5 images" : "Add image"}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setGalleryOpen(true)}
                disabled={images.length >= 5}
              >
                Choose from gallery
              </button>
            </div>
            <GalleryPickerModal
              open={galleryOpen}
              onClose={() => setGalleryOpen(false)}
              kind="image"
              multiple
              maxSelect={5 - images.length}
              alreadySelectedKeys={images.map((img) => img.key)}
              onSelect={handlePickFromGallery}
            />
            <ImagePreviewModal
              open={!!previewImage}
              onClose={() => setPreviewImage(null)}
              url={previewImage?.url}
              fileName={previewImage?.fileName ?? undefined}
            />
            <ImageEditorModal
              open={!!editingImage}
              onClose={() => setEditingImage(null)}
              imageKey={editingImage?.key ?? null}
              url={editingImage?.url ?? null}
              fileName={editingImage?.fileName ?? "image"}
              onApplied={handleImageEdited}
            />
          </>
        )}
      </div>

      <h3 className="panel__section-title">Classification</h3>
      <div className="form-grid">
        <label className="field">
          Category
          <select
            value={form.categoryId}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">— None —</option>
            {topCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Sub-category
          <select
            value={form.subCategoryId}
            onChange={(e) => setField("subCategoryId", e.target.value)}
            disabled={!form.categoryId || subCategories.length === 0}
          >
            <option value="">— None —</option>
            {subCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Brand
          <select
            value={form.brandId}
            onChange={(e) => setField("brandId", e.target.value)}
          >
            <option value="">— None —</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Supplier
          <select
            value={form.supplierId}
            onChange={(e) => setField("supplierId", e.target.value)}
          >
            <option value="">— None —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Manufacturer
          <input
            value={form.manufacturer}
            onChange={(e) => setField("manufacturer", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Tags (comma-separated)
          <input
            value={form.tags}
            onChange={(e) => setField("tags", e.target.value)}
            placeholder="electronics, laptop"
          />
        </label>
      </div>

      <h3 className="panel__section-title">Pricing</h3>
      <div className="form-grid">
        <label className="field">
          Purchase price
          <input
            type="number"
            value={form.purchasePrice}
            onChange={(e) => setField("purchasePrice", e.target.value)}
          />
        </label>
        <label className="field">
          Selling price
          <input
            type="number"
            value={form.sellingPrice}
            onChange={(e) => setField("sellingPrice", e.target.value)}
          />
        </label>
        <label className="field">
          Currency
          <select
            value={form.currency}
            onChange={(e) => setField("currency", e.target.value)}
          >
            {["INR", "USD", "EUR", "GBP", "AED"].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Tax %
          <input
            type="number"
            value={form.taxPercentage}
            onChange={(e) => setField("taxPercentage", e.target.value)}
          />
        </label>
        <label className="field">
          Tax code
          <input
            value={form.taxCode}
            onChange={(e) => setField("taxCode", e.target.value)}
          />
        </label>
        <label className="field">
          Discount %
          <input
            type="number"
            value={form.discountPercentage}
            onChange={(e) => setField("discountPercentage", e.target.value)}
          />
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.isTaxable}
            onChange={(e) => setField("isTaxable", e.target.checked)}
          />
          Taxable
        </label>
      </div>

      <h3 className="panel__section-title">Inventory</h3>
      <div className="form-grid">
        <label className="field">
          Unit
          <input
            value={form.unit}
            onChange={(e) => setField("unit", e.target.value)}
            placeholder="PCS"
          />
        </label>
        <label className="field">
          Inventory tracking
          <select
            value={form.inventoryTracking}
            onChange={(e) => setField("inventoryTracking", e.target.value)}
          >
            {["NONE", "QUANTITY", "SERIAL", "BATCH"].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        {isEdit ? (
          <label className="field">
            Current stock
            <input value={form.totalStock} disabled title="View only" />
            <span className="field__readonly">View only</span>
          </label>
        ) : null}
        <label className="field">
          Reorder level
          <input
            type="number"
            value={form.reorderLevel}
            onChange={(e) => setField("reorderLevel", e.target.value)}
          />
        </label>
        <label className="field">
          Maximum stock level
          <input
            type="number"
            value={form.maximumStockLevel}
            onChange={(e) => setField("maximumStockLevel", e.target.value)}
          />
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.allowBackOrder}
            onChange={(e) => setField("allowBackOrder", e.target.checked)}
          />
          Allow back-order
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.batchTracking}
            onChange={(e) => setField("batchTracking", e.target.checked)}
          />
          Batch tracking
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.serialTracking}
            onChange={(e) => setField("serialTracking", e.target.checked)}
          />
          Serial tracking
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.expiryTracking}
            onChange={(e) => setField("expiryTracking", e.target.checked)}
          />
          Expiry tracking
        </label>
      </div>

      <h3 className="panel__section-title">Logistics</h3>
      <div className="form-grid">
        <label className="field">
          Length
          <input
            type="number"
            value={form.length}
            onChange={(e) => setField("length", e.target.value)}
          />
        </label>
        <label className="field">
          Width
          <input
            type="number"
            value={form.width}
            onChange={(e) => setField("width", e.target.value)}
          />
        </label>
        <label className="field">
          Height
          <input
            type="number"
            value={form.height}
            onChange={(e) => setField("height", e.target.value)}
          />
        </label>
        <label className="field">
          Dimension unit
          <select
            value={form.dimensionUnit}
            onChange={(e) => setField("dimensionUnit", e.target.value)}
          >
            {["cm", "m", "inch"].map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Weight
          <input
            type="number"
            value={form.weight}
            onChange={(e) => setField("weight", e.target.value)}
          />
        </label>
        <label className="field">
          Weight unit
          <select
            value={form.weightUnit}
            onChange={(e) => setField("weightUnit", e.target.value)}
          >
            {["kg", "g", "lb"].map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Country of origin
          <input
            value={form.countryOfOrigin}
            onChange={(e) => setField("countryOfOrigin", e.target.value)}
          />
        </label>
        <label className="field">
          HSN code
          <input
            value={form.hsnCode}
            onChange={(e) => setField("hsnCode", e.target.value)}
          />
        </label>
        <label className="field">
          UPC
          <input
            value={form.upc}
            onChange={(e) => setField("upc", e.target.value)}
          />
        </label>
        <label className="field">
          EAN
          <input
            value={form.ean}
            onChange={(e) => setField("ean", e.target.value)}
          />
        </label>
      </div>

      <h3 className="panel__section-title">Status</h3>
      <div className="form-grid">
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            {["ACTIVE", "INACTIVE", "DISCONTINUED", "DRAFT"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="field field--checkbox">
          <input
            type="checkbox"
            checked={form.isFeatured}
            onChange={(e) => setField("isFeatured", e.target.checked)}
          />
          Featured
        </label>
        <label className="field field--full">
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
};
